import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X } from 'lucide-react';
import './OrderRulesNudge.css';

const DISMISSED_KEY = 'sl-order-rules-nudge-dismissed';

/**
 * Sales Tools — Phase 3.4.
 *
 * Small inline strip on the Orders page that nudges merchants to
 * configure auto-cancel rules when the policy is disabled.
 *
 * Visibility logic:
 *   1. If dismissed this session → hide.
 *   2. Otherwise show eagerly (initial state `needsConfig = true`).
 *   3. Background fetch the policy; hide ONLY when we confirm
 *      enabled === true. Any error (403 perm, 500, network) keeps
 *      the nudge visible with a console.warn so it never silently
 *      disappears.
 *
 * Dismissal is sessionStorage-only — gently reappears next visit so
 * we don't lose the nudge forever after one accidental close.
 */
export default function OrderRulesNudge() {
  // Start visible. Flip to false only if the API confirms the
  // policy is already enabled.
  const [needsConfig, setNeedsConfig] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/api/merchant/store-expiry-policy', {
          credentials: 'include',
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;

        if (!resp.ok) {
          // 403 (no perm) / 500 / etc. Keep the nudge visible — better
          // to over-show than to silently swallow the issue.
          console.warn(
            '[OrderRulesNudge] policy fetch non-OK:',
            resp.status,
            data?.error || ''
          );
          return;
        }
        if (data?.policy?.enabled === true) {
          setNeedsConfig(false);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('[OrderRulesNudge] policy fetch failed:', e.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dismissed]);

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch { /* noop */ }
    setDismissed(true);
  }, []);

  if (dismissed || !needsConfig) return null;

  return (
    <aside className="sl-orn" role="status">
      <div className="sl-orn-icon" aria-hidden>
        <Clock size={14} />
      </div>
      <div className="sl-orn-body">
        <span className="sl-orn-title">Auto-cancel for unpaid orders is off.</span>
        <span className="sl-orn-sub">Keeps your reports and receivables clean.</span>
      </div>
      <Link to="/settings#order-rules" className="sl-orn-cta">
        Set up rules →
      </Link>
      <button
        type="button"
        className="sl-orn-close"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </aside>
  );
}
